import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, User } from 'lucide-react';

interface FeedItem {
  id: string;
  text: string;
  time: string;
  type: 'new' | 'update';
}

interface FeedBarProps {
  items: FeedItem[];
}

const FeedBar: React.FC<FeedBarProps> = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (items.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl p-4 shadow-lg">
        <div className="flex items-center justify-center space-x-2 text-white">
          <TrendingUp className="h-5 w-5" />
          <p className="text-sm font-semibold">En attente de nouveaux RDVs...</p>
        </div>
      </div>
    );
  }

  const currentItem = items[currentIndex];

  return (
    <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl p-4 shadow-lg overflow-hidden">
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2 text-white">
          <TrendingUp className="h-5 w-5 animate-pulse" />
          <span className="text-sm font-bold uppercase tracking-wide">Activité en direct</span>
        </div>

        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentItem.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-xs font-semibold text-white">{currentItem.time}</span>
                </div>
                <p className="text-white font-medium">{currentItem.text}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex space-x-1">
          {items.slice(0, 5).map((_, index) => (
            <div
              key={index}
              className={`h-2 w-2 rounded-full transition-all ${
                index === currentIndex % items.length
                  ? 'bg-white w-4'
                  : 'bg-white/40'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeedBar;
